import React, { Fragment } from 'react'
import {Grid, IconButton, Tooltip, Typography} from '@mui/material'
import { useParams } from 'react-router-dom'
import { DataGrid } from '@mui/x-data-grid'
import useDocuments from '../useCustomerDocuments'
import { formatDate } from '../../../utils/date-formatter'
import { getGridRowClass } from '../../../utils/getGridRowClass'

import Loading from 'shared/loading'
import toast from 'react-hot-toast'
import {Download} from '@mui/icons-material'

const CustomerDocuments = () => {
  const {
    contracts,
    pickupReceipts,
    insuranceCards,
    autoPayAuthorizations,
    images,
    error,
    isLoading,
  }: documentInputProps = useDocuments()

  if (isLoading) {
    return <Loading />
  }
  if (error) {
    return <div>Error on page: {error?.message || error}</div>
  }

  return (
    <Grid container spacing={2} p={3} direction="row">
      <Grid size={{xs:12}}>
        <DGrid
          columns={imageColumns}
          title={'Images'}
          data={images}
          loading={isLoading}
        />
        <DGrid
          columns={contractColumns}
          title={'Contracts'}
          data={contracts}
          loading={isLoading}
        />
        <DGrid
          columns={contractColumns}
          title={'Pickup Receipts'}
          data={pickupReceipts}
          loading={isLoading}
        />
        <DGrid
          columns={contractColumns}
          title={'Insurance Cards'}
          data={insuranceCards}
          loading={isLoading}
        />
        <DGrid
          columns={contractColumns}
          title={'AutoPay Authorizations'}
          data={autoPayAuthorizations}
          loading={isLoading}
        />
      </Grid>
    </Grid>
  )
}

export default CustomerDocuments

const contractColumns = [
  {
    field: 'fileName',
    headerName: 'Name',
    width: 300,
  },
  {
    field: 'creationDate',
    headerName: 'Creation Date',
    width: 150,
    renderCell: (params) => <>{formatDate(params.row.creationDate)}</>,
  },
  {
    field: 'subscriptionId',
    headerName: 'Subscription ID',
    width: 300,
  },
  {
    field: 'documentUri',
    headerName: 'Action',
    width: 150,
    renderCell: (params) => <DownloadDocumentsButton documentId={params.row.id} />,
  },
]

const getImageTypeLabel = (imageType: number): string => {
  switch (imageType) {
    case 0:
      return 'License Front'
    case 1:
      return 'License Back'
    case 2:
      return 'Selfie'
    case 99:
    default:
      return 'Unknown'
  }
}

const imageColumns = [
  {
    field: 'fileName',
    headerName: 'File Name',
    width: 300,
  },
  {
    field: 'imageType',
    headerName: 'Image Type',
    width: 200,
    renderCell: (params) => <>{getImageTypeLabel(params.value as number)}</>,
  },
  {
    field: 'id',
    headerName: 'ID',
    width: 150,
  },
  {
    field: 'uri',
    headerName: 'URI',
    width: 200,
  },
  {
    field: 'presignedUri',
    headerName: 'Action',
    width: 150,
    renderCell: (params) => <DownloadImageButton presignedUri={params.row.presignedUri} />,
  },
  {
    field: 'data',
    headerName: 'Data',
    width: 150,
    renderCell: (params) => params.row.data ? <DownloadImageDataButton imageData={params.row.data} fileName={params.row.fileName} /> : null,
  },
]

const DGrid = ({ title, columns, data, ...rest }) => {
  return (
    <Fragment>
      <Grid size={{xs:12}}>
        <Typography variant="h4">{title}</Typography>
      </Grid>

      <DataGrid
        autoHeight
        rowHeight={35}
        sx={{ pt: 2 }}
        columns={columns}
        getRowClassName={(params) =>
          getGridRowClass(params.indexRelativeToCurrentPage)
        }
        rows={data}
        {...rest}
      />
    </Fragment>
  )
}

const DownloadDocumentsButton = ({ documentId }) => {
  const { useDocumentUri } = useDocuments()
  const { customerId } = useParams<{ customerId: string }>()
  const { refetch } = useDocumentUri(documentId, customerId)

  const onClick = async () => {
    const { data } = await refetch()
    if (data) {
      handleDownload(data)
    }
  }

  return (
    <Tooltip title="Download">
      <IconButton
        className="xsIcon"
        onClick={onClick}
      >
        <Download />
      </IconButton>
    </Tooltip>
  )
}

const DownloadImageButton = ({ presignedUri }) => {

  const onClick = () => {
    if (presignedUri) {
      handleDownload({ documentUri: presignedUri })
    }
  }

  // Only render the button if presignedUri is not empty
  if (!presignedUri) {
    return null
  }

  return (
    <Tooltip title="Download">
      <IconButton
        className="xsIcon"
        onClick={onClick}
      >
        <Download />
      </IconButton>
    </Tooltip>
  )
}

const handleDownload = ({ documentUri }) => {
  if (documentUri) {
    const link = document.createElement('a')
    link.href = documentUri
    link.download = documentUri.split('/').pop()
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  } else {
    console.error('Document URI not found')
    toast.error('Document not found.')
  }
}

const DownloadImageDataButton = ({ imageData, fileName }) => {

  const onClick = () => {
    if (imageData) {
      const link = document.createElement('a')
      link.href = `data:image/jpeg;base64,${imageData}`
      link.download = `${fileName || 'image'}.jpeg`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      console.error('Image data not found')
      toast.error('Image data not found.')
    }
  }

  return (
    <Tooltip title="Download">
      <IconButton
        className="xsIcon"
        onClick={onClick}
      >
        <Download />
      </IconButton>
    </Tooltip>
  )
}

type documentType = {
  creationDate: number
  documentUri: string
  id: string
  subscriptionId: string
  fileName: string
}

type imageType = {
  imageType: number
  data: string
  id: string
  uri: string
  presignedUri: string
  fileName: string
}

type documentInputProps = {
  error: any
  contracts: Array<documentType>
  pickupReceipts: Array<documentType>
  insuranceCards: Array<documentType>
  autoPayAuthorizations: Array<documentType>
  images: Array<imageType>
  isLoading: boolean
}
